import { cloneInstanceClass } from 'helpers/cloneInstanceClass'
import { TransitionAsset } from '~/models/Asset'
import { ASSET_SIZE_STATUS } from '~/config/constants/asset'
import * as Selectors from '~/selectors'
import { isAvailableAttachTransition } from '~/helpers/assets/isAvailableAttachTransition'
import { getTwoSideTransitionInsertStatus } from '~/helpers/assets/getTwoSideTransitionInsertStatus'
import { TRANSITION_MOUNT_TYPE } from '~/enums'

export function onVideoAssetMoved(
  state,
  videoAssetId,
  videoAssetStartTime,
  newStartTime,
  newLayerId
) {
  const [ fadeInOutTransitions,
    transitionsToRemove ] = Selectors.getTransitionsOfMovedAsset(state, videoAssetId)

  // correct start time of fade in/out transitions after moving asset
  fadeInOutTransitions.forEach(tr => {
    // eslint-disable-next-line no-param-reassign
    tr.startTime -= (videoAssetStartTime - newStartTime)

    const dissolveTransitionOnLeftVideo = transitionsToRemove.find(
      t => t.isMountBetween && t.isAttachedToRight(videoAssetId)
    )

    // NOTE: this is for fix fadeout position after removing dissolve asset
    if (dissolveTransitionOnLeftVideo && tr.isAttachedToLeft(videoAssetId)) {
      // eslint-disable-next-line no-param-reassign
      tr.startTime -= dissolveTransitionOnLeftVideo.duration
    }

    // eslint-disable-next-line no-param-reassign
    tr.layerId = newLayerId
  })
}

export function adjustVideoAssetsByTransition(state, transition, isDeleteTransition) {
  if (transition.mountType === TRANSITION_MOUNT_TYPE.BETWEEN) {
    const { leftVideoAsset, rightVideoAsset } = getTwoSideTransitionAssets(state, transition)
    const halfTransition = getHalfTransition(transition.duration)
    let insertType = getTwoSideTransitionInsertStatus(state.assets,
      leftVideoAsset, rightVideoAsset, transition, transition.duration)
    if (!isDeleteTransition && insertType) {
      // eslint-disable-next-line no-param-reassign
      transition.insertType = insertType
      // eslint-disable-next-line no-param-reassign
      transition.leftAssetMountingValues = {
        duration: leftVideoAsset.duration,
        startTime: leftVideoAsset.startTime,
        mediaStart: leftVideoAsset.mediaStart,
      }
      // eslint-disable-next-line no-param-reassign
      transition.rightAssetMountingValues = {
        duration: rightVideoAsset.duration,
        startTime: rightVideoAsset.startTime,
        mediaStart: rightVideoAsset.mediaStart,
      }
    } else if (isDeleteTransition) {
      insertType = transition.insertType
    }

    switch (insertType) {
      case ASSET_SIZE_STATUS.NOT_CUT:
        if (isDeleteTransition) {
          leftVideoAsset.duration -= halfTransition
          rightVideoAsset.startTime += halfTransition
          rightVideoAsset.mediaStart += halfTransition
          rightVideoAsset.duration -= halfTransition
        } else {
          rightVideoAsset.startTime -= transition.duration
          moveConnectedAssetsWithDissolve(transition.id, rightVideoAsset,
            rightVideoAsset.layerId, state, transition.duration)
        }
        break
      case ASSET_SIZE_STATUS.LEFT_CUT_RIGHT_NORMAL:
        if (isDeleteTransition) {
          leftVideoAsset.duration -= transition.duration
        } else {
          leftVideoAsset.duration += transition.duration
        }
        break
      case ASSET_SIZE_STATUS.RIGHT_CUT_LEFT_NORMAL:
        if (isDeleteTransition) {
          rightVideoAsset.duration -= transition.duration
          rightVideoAsset.startTime += transition.duration
          rightVideoAsset.mediaStart += transition.duration
        } else {
          rightVideoAsset.duration += transition.duration
          rightVideoAsset.startTime -= transition.duration
          rightVideoAsset.mediaStart -= transition.duration
        }
        break
      case ASSET_SIZE_STATUS.LEFT_CUT_RIGHT_CUT:
        if (isDeleteTransition) {
          rightVideoAsset.duration -= halfTransition
          rightVideoAsset.startTime += halfTransition
          rightVideoAsset.mediaStart += halfTransition
          leftVideoAsset.duration -= halfTransition
        } else {
          rightVideoAsset.duration += halfTransition
          rightVideoAsset.startTime -= halfTransition
          rightVideoAsset.mediaStart -= halfTransition
          leftVideoAsset.duration += halfTransition
        }
        break
      case ASSET_SIZE_STATUS.LEFT_CUT_RIGHT_CUT_ASYMMETRICAL:
        if (isDeleteTransition) {
          rightVideoAsset.mediaStart = transition.rightAssetMountingValues.mediaStart
          rightVideoAsset.startTime = transition.rightAssetMountingValues.startTime
          leftVideoAsset.duration = transition.leftAssetMountingValues.duration
        } else {
          rightVideoAsset.mediaStart -= leftVideoAsset.duration
          rightVideoAsset.startTime -= leftVideoAsset.duration
          leftVideoAsset.duration += (transition.duration - leftVideoAsset.duration)
        }
        break
      default:
        break
    }
    state.assets = state.assets.map(asset => {
      if (asset.id === leftVideoAsset.id) return leftVideoAsset
      if (asset.id === rightVideoAsset.id) return rightVideoAsset
      return asset
    })
  }
}

export function deleteTransitions(state, transitions) {
  const { uniqTransitionAssets } = getUniqTransitions(transitions)
  state.assets = state.assets
    .filter(asset => !uniqTransitionAssets.find(transition => transition.id === asset.id))
  uniqTransitionAssets
    .forEach(transition => adjustVideoAssetsByTransition(state, transition, true))
}

export function attachTransitionToVideo(state, {
  transitionId,
  leftAssetId,
  rightAssetId,
}) {
  const transition = state.assets.find(el => el.id === transitionId)
  const rightAsset = state.assets.find(el => el.id === rightAssetId)
  const leftAsset = state.assets.find(el => el.id === leftAssetId)

  if (leftAsset && isAvailableAttachTransition(leftAsset)) {
    transition.attachTo(leftAssetId, 'left')
  }

  if (rightAsset && isAvailableAttachTransition(rightAsset)) {
    if (transition && !transition.isAttachedToRight(rightAssetId)) {
      transition.attachTo(rightAssetId, 'right')
    }
  }

  const videoRef = state.assets.find(el => el.id === (leftAssetId || rightAssetId))

  if (videoRef) {
    transition.layerId = videoRef.layerId
  }
  adjustVideoAssetsByTransition(state, transition)
}

function getTwoSideTransitionAssets(state, transition) {
  const leftVideoAsset = state.assets.find(el => transition.isAttachedToLeft(el.id))
  const rightVideoAsset = state.assets.find(el => transition.isAttachedToRight(el.id))
  return { leftVideoAsset, rightVideoAsset }
}

function getUniqTransitions(transitions) {
  return transitions.reduce((result, transition) => {
    if (!result.uniqTransitionIds.includes(transition.id)) {
      result.uniqTransitionIds.push(transition.id)
      result.uniqTransitionAssets.push(cloneInstanceClass(transition))
    }
    return result
  }, { uniqTransitionAssets: [], uniqTransitionIds: [] })
}

function getHalfTransition(transitionDuration) {
  return Math.ceil(Number(transitionDuration) / 2)
}

function moveConnectedAssetsWithDissolve(transitionId, connectedAsset, layerId, state, offset) {
  let rightConnectedDissolveAsset = connectedAsset
  const restTransitions = state.assets.filter(asset => (asset instanceof TransitionAsset)
      && (asset.id !== transitionId) && (layerId === asset.layerId))
    .sort((a, b) => a.startTime - b.startTime)

  if (rightConnectedDissolveAsset) {
    restTransitions.forEach(transition => {
      if (transition.isAttachedToLeft(rightConnectedDissolveAsset.id)) {
        const { rightVideoAsset } = getTwoSideTransitionAssets(state, transition)
        if (rightVideoAsset) {
          rightVideoAsset.startTime -= offset
          rightConnectedDissolveAsset = rightVideoAsset
        }
        // eslint-disable-next-line no-param-reassign
        transition.startTime -= offset
      }
    })
  }
}
